import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, TextAreaController } from '@organisms';
import { Button, Col, Collapse, Divider, Form, Row, Typography } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload';
import React, { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { CancelationButtonTitleFormFields } from 'src/features/Funnel/Components/formFields/CancelationButtonTitleFormFields';
import { LayoutFunnelPage } from 'src/features/Funnel/Components/LayoutFunnelPage';
import { TitleFunnelPage } from 'src/features/Funnel/Components/TitleFunnelPage';
import { UploadImage } from 'src/features/Funnel/Components/UploadImage';
import { useRequestReleasePage } from 'src/features/Funnel/hooks/release/useRequestReleasePage';
import { useTryToRequestCreatingReleaseFunnelPage } from 'src/features/Funnel/hooks/release/useTryToRequestCreatingReleaseFunnelPage';

const slideImagesSchema = yup.object({
  web: yup
    .mixed<string | UploadChangeParam<UploadFile>>()
    .required('A Versão Web é obrigatório')
    .test('is-filled', 'A Versão Web é obrigatória', function (value) {
      return value !== null && value !== '';
    }),
  mobile: yup
    .mixed<string | UploadChangeParam<UploadFile>>()
    .required('A Versão mobile é obrigatório')
    .test('is-filled', 'A Versão mobile é obrigatória', function (value) {
      return value !== null && value !== '';
    }),
  link: yup.string().required('O Link é obrigatório'),
});

const slidesSchema = yup.object({
  title: yup.string().required('O Título é obrigatório'),
  subtitle: yup.string().required('O Subtítulo é obrigatório'),
  slidesImages: yup
    .array()
    .of(slideImagesSchema)
    .required('Informe ao menos uma imagem'),
});

const validationSchema = yup.object({
  cancellationFunnelId: yup
    .string()
    .required('cancellationFunnelId é obrigatório'),
  slides: yup.array().of(slidesSchema).required('Informe ao menos um slide'),
  continueCancelationButtonTitle: yup.string().nullable(),
  keepSubscriptionButtonTitle: yup.string().nullable(),
});

export type ReleasePageFormData = yup.InferType<typeof validationSchema>;
export type ReleasePageFormDataWithName = ReleasePageFormData & {
  name: string;
};

export const FunnelReleasePageScreen: React.FC = () => {
  const [titlePage, setTitlePage] = useState('Modal de Lançamento');
  const [isUpdate, setIsUpdate] = useState(false);

  const navigate = useNavigate();

  const { control, handleSubmit, setValue } = useForm<ReleasePageFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      cancellationFunnelId: '',
      slides: [
        {
          title: '',
          subtitle: '',
          slidesImages: [
            {
              web: '',
              mobile: '',
              link: '',
            },
            {
              web: '',
              mobile: '',
              link: '',
            },
            {
              web: '',
              mobile: '',
              link: '',
            },
          ],
        },
      ],
      continueCancelationButtonTitle: null,
      keepSubscriptionButtonTitle: null,
    },
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'slides',
  });

  const { id } = useParams();
  const { isLoadingCreatingRelease, tryToRequestCreatingReleaseFunnelPage } =
    useTryToRequestCreatingReleaseFunnelPage();
  const { isLoadingReleaseUpdate, handleUpdateReleasePage } =
    useRequestReleasePage({
      isUpdate,
      setIsUpdate,
      setTitlePage,
      titlePage,
      setValue: setValue,
      replace: replace,
    });

  const handleReleasePage = useCallback(
    async (e: ReleasePageFormData) => {
      if (isUpdate) {
        return handleUpdateReleasePage(e);
      }
      await tryToRequestCreatingReleaseFunnelPage({
        name: titlePage,
        ...e,
      });
    },
    [
      isUpdate,
      handleUpdateReleasePage,
      tryToRequestCreatingReleaseFunnelPage,
      titlePage,
    ]
  );

  useEffect(() => {
    setValue('cancellationFunnelId', id ?? '');
  }, [id]);

  const [activeSlide, setActiveSlide] = useState([fields[0].id]);
  useEffect(() => {
    setActiveSlide([fields[0].id]);
  }, [fields]);

  return (
    <LayoutFunnelPage>
      <TitleFunnelPage titlePage={titlePage} setTitlePage={setTitlePage} />
      <Row>
        <Form
          layout='vertical'
          requiredMark={'optional'}
          style={{
            width: '100%',
            gap: '24px',
            display: 'flex',
            flexDirection: 'column',
          }}
          onFinish={handleSubmit(handleReleasePage)}
        >
          <Collapse
            activeKey={activeSlide}
            onChange={e => setActiveSlide(e as any)}
            expandIconPosition={'end'}
          >
            {fields.map((field, index) => (
              <Collapse.Panel
                header={`Slide ${index + 1}`}
                key={field.id}
                extra={
                  <Button
                    type='text'
                    style={{
                      minWidth: 'auto',
                      padding: '1px 6px',
                    }}
                    onClick={() => remove(index)}
                    disabled={fields.length <= 1}
                  >
                    <DeleteOutlined
                      style={{
                        fontSize: '16px',
                        cursor: 'pointer',
                      }}
                    />
                  </Button>
                }
              >
                <Row style={{ flexDirection: 'column', gap: '24px' }}>
                  <Controller
                    control={control}
                    name={`slides[${index}].title`}
                    placeholder='Escreva o principal título da página.'
                    defaultValue={field.title}
                    autoComplete='off'
                    label='Título Principal'
                    showCount={true}
                    maxLength={250}
                    required
                  />
                  <TextAreaController
                    control={control}
                    name={`slides[${index}].subtitle`}
                    placeholder='Área de texto'
                    defaultValue={field.subtitle}
                    autoComplete='off'
                    label='Subtítulo'
                    isMaxLength={true}
                    maxLength={250}
                    row={2}
                    required={false}
                  />
                  {field.slidesImages.map((imageData, imageIndex) => (
                    <Row style={{ flexDirection: 'column' }}>
                      <Col
                        style={{
                          gap: '24px',
                          display: 'flex',
                          flexDirection: 'row',
                        }}
                        key={imageData.web as string}
                      >
                        <Col style={{ flex: 1 }}>
                          <UploadImage
                            tooltip={{
                              title: () => (
                                <Typography.Text>
                                  <strong>Versão Web</strong> - 482 x 282 pixels
                                  (jpg, jpeg, png)
                                </Typography.Text>
                              ),
                              icon: <InfoCircleOutlined />,
                            }}
                            control={control}
                            name={`slides[${index}].slidesImages[${imageIndex}].web`}
                            label='Versão Web'
                            required
                          />
                        </Col>
                        <Col style={{ flex: 1 }}>
                          <UploadImage
                            tooltip={{
                              title: () => (
                                <Typography.Text>
                                  <strong>Versão App</strong> - 343 x 124 pixels
                                  (jpg, jpeg, png)
                                </Typography.Text>
                              ),
                              icon: <InfoCircleOutlined />,
                            }}
                            control={control}
                            name={`slides[${index}].slidesImages[${imageIndex}].mobile`}
                            label='Versão Mobile'
                            required
                          />
                        </Col>
                      </Col>
                      <Controller
                        control={control}
                        name={`slides[${index}].slidesImages[${imageIndex}].link`}
                        placeholder='Link da imagem'
                        defaultValue={imageData.link}
                        autoComplete='off'
                        label='Link da imagem'
                        required
                        prefix={
                          <ThunderboltOutlined style={{ color: '#1668DC' }} />
                        }
                        showCount={true}
                        maxLength={250}
                      />
                    </Row>
                  ))}
                </Row>
              </Collapse.Panel>
            ))}
          </Collapse>
          <Row style={{ justifyContent: 'center' }}>
            <Col>
              <Button
                type='text'
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  append({
                    title: '',
                    subtitle: '',
                    slidesImages: [
                      {
                        web: '',
                        mobile: '',
                        link: '',
                      },
                      {
                        web: '',
                        mobile: '',
                        link: '',
                      },
                      {
                        web: '',
                        mobile: '',
                        link: '',
                      },
                    ],
                  });
                }}
                disabled={fields.length >= 5}
              >
                Adicionar mais uma opção
              </Button>
            </Col>
          </Row>
          <CancelationButtonTitleFormFields control={control} />
          <Divider orientation='center' style={{ marginTop: '398px' }} />
          <Row style={{ justifyContent: 'end' }}>
            <Row style={{ gap: '8px' }}>
              <Button type='default' onClick={() => navigate(-1)}>
                Cancelar
              </Button>
              <Button
                type='primary'
                htmlType='submit'
                loading={
                  isUpdate ? isLoadingReleaseUpdate : isLoadingCreatingRelease
                }
              >
                Salvar
              </Button>
            </Row>
          </Row>
        </Form>
      </Row>
    </LayoutFunnelPage>
  );
};
